/**
 * @fileoverview Is this author already using this name, under EITHER kind?
 *
 * WHY THIS IS NOT TIDINESS. A page's readable URL is `<author>/<slug>`, and we
 * resolve it by looking in BOTH kinds. That promise only holds while an author
 * has at most one page per slug. Let a 30023 and a 33301 share a name and the
 * readable URL stops naming a page: it names two, and whichever the resolver
 * happens to prefer becomes the answer.
 *
 * WHAT THE 2026-09-16 RULING TOOK OUT OF THAT, AND WHAT IT LEFT. Both kinds
 * used to be ours to produce -- markdown was a 30023 and a blob-backed page was
 * a 33301 -- so the ordinary collision was an author's own two pages, and this
 * check was also what let a readable link survive a flip. Every page is a 33301
 * now, nothing we publish can land on 30023, and that collision is gone along
 * with the flip it protected. The one left is the one we do not control: a
 * long-form article the author wrote in some other client, sitting at the same
 * name. Still ambiguous, still refused, and no longer something we could have
 * avoided by publishing differently.
 *
 * It is checked NOW, while almost nothing exists to migrate. A uniqueness rule
 * added after a thousand pages have been published is not a rule, it is a
 * migration, and the readable URL would have been ambiguous the whole time.
 *
 * WHY A TARGETED QUERY AND NOT THE AUTHOR INDEX NEXT DOOR. `loadAuthorIndex`
 * caps at a couple of hundred events per kind and drops anything it cannot
 * parse. Both are right for a list on a screen and wrong here: a collision with
 * the author's 300th page is still a collision, and an encrypted private page
 * still OCCUPIES its slug even though its tags are ciphertext -- its `d` tag
 * stays plaintext, because the address is what makes the page reachable at all.
 * Asking for one author, one kind and one `d` at a time finds both, and stays
 * inside the relay's narrow-filter limits.
 *
 * WHAT THIS CANNOT DO, STATED RATHER THAN HIDDEN. There is no server, so there
 * is no authority to ask. A relay that times out, refuses the filter or simply
 * does not hold the author's other pages answers with nothing, which is
 * indistinguishable from the slug being free. So a "free" answer here is BEST
 * EFFORT, which is right for RESOLVING a readable link: a slow relay must not
 * make a page vanish.
 *
 * IT IS NO LONGER WHAT GUARDS PUBLISHING. Until 2026-10-09 the publish path
 * used this too, and a relay that did not answer let the publish through, so
 * that a hiccup could never stop an author saving. cloistr-orchestrator ruled
 * otherwise from cloistr-common's overwrite sweep: a relay failure is an
 * error, not "free". Publishing now asks `checkName` in nameCheck.ts, which
 * knows which relays actually answered.
 */

import type { Event } from 'nostr-tools';

import { NIP23_LONG_FORM_KIND, PAGE_INDEX_KIND } from './kinds';

import { fetchAll } from '@/services/nostr/relays';

/** An event already sitting at this author's `<slug>`, and which kind it is. */
export interface SlugHolder {
  kind: number;
  pubkey: string;
  identifier: string;
  /**
   * When it was published. The publish check ignores this and only asks whether
   * the list is empty. It is here for `resolveReadable.ts`, which compares it
   * ACROSS the two kinds: if an author somehow holds one name twice, the
   * readable link has to name one page, and the newest is the right one because
   * the case that produces duplicates on purpose -- a public page flipped to
   * private -- makes the new kind the later event.
   */
  createdAt: number;
}

/**
 * Every page this author already has at `identifier`, across both page kinds.
 *
 * Returns an empty list when the slug is free AND when nothing answered. The
 * caller cannot tell those apart and must not pretend it can -- see the note
 * above on why that is acceptable and what it costs.
 */
export async function findSlugHolders(
  pubkey: string,
  identifier: string,
  relays: string[]
): Promise<SlugHolder[]> {
  if (relays.length === 0) return [];

  const kinds = [NIP23_LONG_FORM_KIND, PAGE_INDEX_KIND];

  const answers = await Promise.all(
    kinds.map((kind) =>
      // One kind, one author, one `d`. cloistr-relay answers a filter it
      // considers complex with a bare CLOSED and no partial result, which would
      // look exactly like the slug being free.
      fetchAll(relays, { kinds: [kind], authors: [pubkey], '#d': [identifier], limit: 1 })
    )
  );

  return kinds.flatMap((kind, i) => {
    // Newest WITHIN the kind, for the same reason `fetchNewest` exists: two
    // relays can hold two versions of one replaceable event, and the one that
    // answered first is whichever socket was quicker rather than whichever is
    // current.
    const newest = (answers[i] ?? []).reduce<Event | null>(
      (best, e) => (!best || e.created_at > best.created_at ? e : best),
      null
    );
    return newest ? [{ kind, pubkey, identifier, createdAt: newest.created_at }] : [];
  });
}
